import React, { createContext, useContext, useEffect, useState } from 'react'
import { User } from '@supabase/supabase-js'
import { useNavigate, useLocation } from 'react-router-dom'
import { isAuthenticated, getCurrentUser, signOut } from '../lib/supabase'

interface AuthContextType {
  isAdmin: boolean
  loading: boolean
  user: User | null
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>({
  isAdmin: false,
  loading: true,
  user: null,
  signOut: async () => {}
})

type AuthProviderProps = {
  children: React.ReactNode;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    async function checkAuth() {
      try {
        const [authenticated, currentUser] = await Promise.all([
          isAuthenticated(),
          getCurrentUser()
        ])
        
        setIsAdmin(authenticated)
        setUser(currentUser)

        if (!authenticated && location.pathname !== '/login') {
          navigate('/login')
        }
      } catch (error) {
        console.error('Auth check failed:', error)
        setIsAdmin(false)
        setUser(null)
        if (location.pathname !== '/login') {
          navigate('/login')
        }
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [navigate, location])

  const handleSignOut = async () => {
    await signOut()
    setUser(null)
    setIsAdmin(false)
    navigate('/login')
  }

  return (
    <AuthContext.Provider value={{ isAdmin, loading, user, signOut: handleSignOut }}>
      {!loading && children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 